import React, { Component } from 'react';
import { Field } from 'formik';
import * as yup from 'yup';
import { withAsyncActions } from 'react-async-client';
import { append, assocPath, includes, pathOr, without } from 'ramda';
import { Checkbox, Spin } from 'antd';

import ModalForm from 'components/common/ModalForm';
import { getProductCategories, getProductDepartments, getProductGroups, getProductsList, putSalePlanDepartments } from 'actions/asyncActions';
import { formAutoOptions, fullAutoOptions } from 'constants/asyncClient';
import SearchSelect from '../../common/form/SearchSelect';
import Input from '../../common/form/Input';
import ColorPicker from '../../common/form/ColorPicker';
import NumberInput from '../../common/form/NumberInput';
import SwitchInput from '../../common/form/SwitchInput';
import RadioGroup from '../../common/form/RadioGroup';
import { FOCUS_TYPES } from 'constants/focus';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';

@withAsyncActions({
    getProductsList: getProductsList
        .withPayload(({ productCategory }) => ({
            q: {
                productCategory
            },
            limit: 0
        }))
        .withParams(({ productCategory }) => productCategory)
        .withOptions(fullAutoOptions)
})
class ProductsField extends Component {
    componentDidUpdate(prev) {
        const { getProductsList: { data } } = this.props;
        const productCategories = pathOr([], ['values', 'productCategories'], this.props.formProps);
        const productGroups = pathOr([], ['values', 'productGroups'], this.props.formProps);
        const prevProductCategories = pathOr([], ['values', 'productCategories'], prev.formProps);
        const prevProductGroups = pathOr([], ['values', 'productGroups'], prev.formProps);
        const prevCheckedCategory = includes(prev.productCategory, prevProductCategories);
        const checkedCategory = includes(this.props.productCategory, productCategories);
        const prevCheckedGroup = includes(prev.productGroup, prevProductGroups);
        const checkedGroup = includes(this.props.productGroup, productGroups);

        if ((prevCheckedCategory !== checkedCategory && checkedCategory) || (prevCheckedGroup !== checkedGroup && checkedGroup)) {
            this.props.formProps.setFieldValue('products', without((data.items || []).map(({ id }) => id), pathOr([], ['values', 'products'], this.props.formProps)));
        }
    }

    getCheckedValues = () => {
        return pathOr([], ['values', 'products'], this.props.formProps);
    }

    onChange = e => {
        const checkedValues = this.getCheckedValues();
        const value = e.target.value;
        const isCurrentChecked = includes(value, checkedValues);

        this.props.formProps.setFieldValue('products', isCurrentChecked ? without([value], checkedValues) : append(value, checkedValues));
    }

    isParentChecked = () => {
        const productCategories = pathOr([], ['values', 'productCategories'], this.props.formProps);
        const productGroups = pathOr([], ['values', 'productGroups'], this.props.formProps);

        return includes(this.props.productCategory, productCategories) || includes(this.props.productGroup, productGroups);
    }

    render() {
        const { getProductsList: { data, meta } } = this.props;
        const checkedValues = this.getCheckedValues();

        return meta.pending ? <Spin size='small' /> :
            <Checkbox.Group
                value={checkedValues}
            >
                { (data.items || []).map((item) => {
                    return (
                        <div key={`product-${item.id}`} style={{ marginBottom: 8 }}>
                            <Checkbox disabled={this.isParentChecked()} value={item.id} onChange={this.onChange}>{ item.name }</Checkbox>
                        </div>
                    );
                })}
            </Checkbox.Group>
    }
}

@withAsyncActions({
    getProductCategories: getProductCategories
        .withPayload(({ productGroup }) => ({
            q: {
                productGroup
            },
            limit: 0
        }))
        .withParams(({ productGroup }) => productGroup)
        .withOptions(fullAutoOptions)
})
class CategoriesField extends Component {
    state = {
        expanded: []
    };

    componentDidUpdate(prev) {
        const { getProductCategories: { data } } = this.props;
        const productGroups = pathOr([], ['values', 'productGroups'], this.props.formProps);
        const prevProductGroups = pathOr([], ['values', 'productGroups'], prev.formProps);
        const prevCheckedGroup = includes(prev.productGroup, prevProductGroups);
        const checkedGroup = includes(this.props.productGroup, productGroups);

        if (prevCheckedGroup !== checkedGroup && checkedGroup) {
            this.props.formProps.setFieldValue('productCategories', without((data.items || []).map(({ id }) => id), pathOr([], ['values', 'productCategories'], this.props.formProps)));
        }
    }

    toggleExpand = id => {
        this.setState(({ expanded }) => ({ expanded: includes(id, expanded) ? without([id], expanded) : append(id, expanded) }));
    }

    getCheckedValues = () => {
        return pathOr([], ['values', 'productCategories'], this.props.formProps);
    }

    onChange = e => {
        const checkedValues = this.getCheckedValues();
        const value = e.target.value;
        const isCurrentChecked = includes(value, checkedValues);

        this.props.formProps.setFieldValue('productCategories', isCurrentChecked ? without([value], checkedValues) : append(value, checkedValues));
    }

    isParentChecked = () => {
        const productGroups = pathOr([], ['values', 'productGroups'], this.props.formProps);

        return includes(this.props.productGroup, productGroups);
    }

    render() {
        const { getProductCategories: { data, meta }, productGroup, formProps } = this.props;
        const checkedValues = this.getCheckedValues();

        return meta.pending ? <Spin size='small' /> :
            <Checkbox.Group
                value={checkedValues}
            >
                { (data.items || []).map((item) => {
                    const isExpanded = includes(item.id, this.state.expanded);

                    return (
                        <div key={`category-${item.id}`} style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span
                                    style={{
                                        cursor: "pointer",
                                        marginRight: 8,
                                        display: "inline-flex",
                                        alignItems: "center",
                                    }}
                                    onClick={() => this.toggleExpand(item.id)}
                                >
                                    {isExpanded ? <MinusOutlined /> : <PlusOutlined />}
                                </span>

                                <Checkbox disabled={this.isParentChecked()} value={item.id} onChange={this.onChange}>{ item.name }</Checkbox>
                            </div>

                            { isExpanded && (
                                <div style={{ marginLeft: 44, marginTop: 4 }}>
                                    <ProductsField productGroup={productGroup} productCategory={item.id} formProps={formProps} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </Checkbox.Group>
    }
}

@withAsyncActions({
    getProductGroups: getProductGroups
        .withPayload(({ productDepartment }) => ({
            q: {
                productDepartment
            },
            limit: 0
        }))
        .withOptions(fullAutoOptions)
})
class GroupsField extends Component {
    state = {
        expanded: []
    };

    toggleExpand = id => {
        this.setState(({ expanded }) => ({ expanded: includes(id, expanded) ? without([id], expanded) : append(id, expanded) }));
    }

    getCheckedValues = () => {
        return pathOr([], ['values', 'productGroups'], this.props.formProps);
    }

    onChange = values => {
        this.props.formProps.setFieldValue('productGroups', values);
    }

    render() {
        const { getProductGroups: { data, meta }, formProps } = this.props;
        const checkedValues = this.getCheckedValues();

        return meta.pending ? <Spin size='small' /> :
            <Checkbox.Group
                value={checkedValues}
                onChange={this.onChange}
            >
                { (data.items || []).map((item) => {
                    const isExpanded = includes(item.id, this.state.expanded);

                    return (
                        <div key={`group-${item.id}`} style={{ marginBottom: 8 }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span
                                    style={{
                                        cursor: "pointer",
                                        marginRight: 8,
                                        display: "inline-flex",
                                        alignItems: "center",
                                    }}
                                    onClick={() => this.toggleExpand(item.id)}
                                >
                                    {isExpanded ? <MinusOutlined /> : <PlusOutlined />}
                                </span>

                                <Checkbox value={item.id}>{ item.name }</Checkbox>
                            </div>

                            { isExpanded && (
                                <div style={{ marginLeft: 24, marginTop: 4 }}>
                                    <CategoriesField productGroup={item.id} formProps={formProps} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </Checkbox.Group>
    }
}

@withAsyncActions(() => ({
    putAction: putSalePlanDepartments
}), formAutoOptions)
export default class FocusCategoriesProjectModal extends Component {
    renderForm = formProps => {
        return (
            <div>
                <Field
                    name='name'
                    component={Input}
                    label='Название:' />
                <Field
                    name='color'
                    component={ColorPicker}
                    label='Цвет:' />
                <Field
                    name='type'
                    component={RadioGroup}
                    optionType='button'
                    options={FOCUS_TYPES}
                    itemClassName='focus-form-type-field'
                    label='Тип:' />
                <Field
                    name='productDepartment'
                    component={SearchSelect}
                    label='Проект:'
                    placeholder='Выберите проект'
                    onChange={() => formProps.setFieldValue('productGroups', undefined)}
                    action={getProductDepartments} />
                { !!formProps.values.productDepartment && <GroupsField productDepartment={formProps.values.productDepartment} formProps={formProps} /> }
                <Field
                    name='salePlanBonusOptions.enabled'
                    component={SwitchInput}
                    label='Бонусный интервал:' />
                { formProps.values.salePlanBonusOptions && formProps.values.salePlanBonusOptions.enabled ? (
                    <Field
                        name='salePlanBonusOptions.percents'
                        component={NumberInput}
                        label='Бонусный процент:' />
                ) : null }
            </div>
        );
    }

    getValidationSchema() {
        return yup.object().shape({
            name: yup.string().trim().required(),
            productDepartment: yup.string().required()
        });
    }

    mapBeforeSubmit = values => {
        const { edit, index, ...data } = this.props.params;

        return edit ? {
            ...data,
            departmentPlans: assocPath([index], values, data.departmentPlans),
            salePlanBonusOptions: values.salePlanBonusOptions,
        } : {
            ...data,
            departmentPlans: append(values, data.departmentPlans),
            salePlanBonusOptions: values.salePlanBonusOptions,
        }
    }

    render() {
        const { params } = this.props;

        return (
            <ModalForm
                {...this.props}
                params={params.edit ? params.departmentPlans[params.index] : { type: 'amount' }}
                mapBeforeSubmit={this.mapBeforeSubmit}
                editTitle={params.edit ? 'Редактировать проект' : 'Добавить проект'}
                validationSchema={this.getValidationSchema()}
                renderForm={this.renderForm}
                isEdit
            />
        );
    }
}
